import { useState, useEffect, useRef } from 'react'
import { Calendar } from 'lucide-react'
import SimpleCalendar from '../../calendar/SimpleCalendar'

interface DateFieldProps {
    name: string
    value: string
    onChange: (value: string) => void
    setValue: (field: string, value: string) => void
}

const DateField: React.FC<DateFieldProps> = ({ name, value, onChange, setValue }) => {
    const [showCalendar, setShowCalendar] = useState(false)
    const [inputValue, setInputValue] = useState(value)
    const inputRef = useRef<HTMLInputElement | null>(null)
    const calendarRef = useRef<HTMLDivElement | null>(null)
    const buttonRef = useRef<HTMLButtonElement | null>(null)

    // Máscara para a data
    const formatDateMask = (value: string) => {
        const formattedValue = value
            .replace(/\D/g, '') // Remove tudo que não é número
            .replace(/(\d{2})(\d{2})(\d{4})/, '$1/$2/$3') // Adiciona a máscara dd/mm/yyyy
            .slice(0, 10) // Garante que o máximo de caracteres é 10

        setValue(name, formattedValue) // Atualiza o valor no react-hook-form
        return formattedValue
    }

    // Handle da mudança do input
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value
        setInputValue(newValue)
        // Só atualiza se a data for válida
        if (newValue.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)) {
            setValue(name, newValue) // Atualiza o valor no react-hook-form
            onChange(newValue) // Também passa para o onChange de fora
        }
    }

    // Função para formatar a data no formato dd/mm/yyyy
    const formatDate = (date: string): string => {
        const [year, month, day] = date.split('-') // Divide a data no formato yyyy-mm-dd
        return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}` // Retorna no formato dd/mm/yyyy
    }

    // Handle da seleção de data
    const handleDateSelect = (date: string) => {
        const formattedDate = formatDate(date)
        setInputValue(formattedDate)
        setValue(name, formattedDate) // Atualiza o valor no react-hook-form
        onChange(formattedDate) // Passa para o onChange de fora
        setShowCalendar(false) // Fecha o calendário após seleção
    }

    // Toggle de exibição do calendário
    // Toggle de exibição do calendário
    const toggleCalendar = (event: React.MouseEvent) => {
        event.stopPropagation() // Evita propagação do clique para o document
        setTimeout(() => {
            setShowCalendar(prev => !prev)
        }, 0)
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                calendarRef.current &&
                !calendarRef.current.contains(event.target as Node) &&
                inputRef.current &&
                !inputRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setShowCalendar(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    return (
        <div className='relative'>
            <input
                ref={inputRef}
                type='text'
                value={formatDateMask(inputValue)} // Aplica a máscara no input
                onChange={handleInputChange}
                onClick={toggleCalendar}
                placeholder='dd/mm/aaaa'
                className='w-full px-4 py-2.5 border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white'
            />
            <button
                ref={buttonRef}
                type='button'
                onClick={toggleCalendar}
                className='absolute right-6 top-2.5 text-gray-500 dark:text-gray-400'
            >
                <Calendar className='w-5 h-5' />
            </button>

            {showCalendar && (
                <div
                    ref={calendarRef}
                    className='absolute z-10 bg-white dark:bg-gray-800 shadow-lg border dark:border-gray-700 dark:text-gray-200 rounded-lg p-4 mt-1'
                >
                    <SimpleCalendar value={formatDateMask(inputValue)} onDateSelect={handleDateSelect} />
                </div>
            )}
        </div>
    )
}

export default DateField
